import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import classNames from 'classnames';

import { 
    CurrencyInput, 
    ErrorMessageContainer, 
    InputController, 
    MessagePropType, 
    MessageStyles, 
    PercentageInput, 
    Sizes, 
    hasSameValues,
    useSafeLayoutEffect,
} from '@ratehub/base-ui';

import { getMinimumDownPaymentDollars, getMinimumDownPaymentPercent } from '../functions/getMinimumDownPayment';
import roundDownPaymentPercentage from '../functions/roundDownPaymentPercentage';


function DownPaymentInput({
    id,
    name,
    value,

    purchasePrice,

    onChange,
    onFocus,
    onBlur,
    onValidityChange,
    onValidate,

    isRequired,
    isDisabled,

    isInline,
    isCompact,

    inputClassName,

    percentProps,
    dollarsProps,

    errorMessageStyle,

    canShowInvalid,

    ...otherProps
}) {
    const state = useDownPaymentState({
        value,
        purchasePrice,
        getCustomErrorMessage: onValidate,
        canShowInvalid,
    });

    // Computing the error message in state to prevent flashing error message; everything needs to be in sync or the error message will be computed before state is updated
    const getErrorMessage = () => state.errorMessage;

    // formula being used to provide better UX than snapping UX (which doesn't wait for user input to finish)
    const formula = () => state.minimumDownPayment;
    
    return (
        <InputController
            {...otherProps}
            id={id ?? name}
            name={name}
            value={state.value}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onValidityChange={onValidityChange}
            isRequired={isRequired}
            isDisabled={isDisabled}
            getParsedValue={formattedValue => purchasePrice ? formattedValue : null} // we can't parse a down payment % if we don't have a purchase price
            formula={formula}
            canShowInvalid={state.canShowInvalid}
        >
            {(inputProps, inputState) => {
                const commonProps = {
                    errorMessageStyle: MessageStyles.MINIMAL, // we're displaying the error messages around both inputs
                    isRequired: inputProps.required,
                    isDisabled: inputProps.disabled,
                    onFocus,
                    onBlur: inputProps.onBlur,
                    defaultErrorMessage: DEFAULT_ERROR_MESSAGE,
                    inputClassName,
                };

                function handleDollarsChange(newDollars) {
                    state.dollars = newDollars;
                    inputProps.onChange(state.value);
                }

                function handlePercentChange(newPercent) {
                    state.percent = newPercent;
                    inputProps.onChange(state.value);
                }

                return (
                    <Container
                        className={classNames(inputProps.className, {
                            'is-inline': isInline,
                        })}
                    >
                        <ErrorMessageContainer
                            message={inputState.errorMessage}
                            isInvalid={inputState.shouldShowInvalid}
                            className="input-container"
                            messageStyle={errorMessageStyle}
                        >
                            <PercentageInput
                                {...commonProps}
                                decimalScale={2}
                                label={(
                                    <If condition={isCompact}>
                                        <FormattedMessage
                                            id="DownPaymentInput.labels.percent"
                                            defaultMessage="%"
                                        />
                                    </If>
                                )}
                                isPercentSymbolVisible
                                {...percentProps}
                                //! the following props can't be overridden as they are required for the component to function properly
                                name={`${name}.percent`}
                                value={inputProps.value?.percent}
                                onChange={handlePercentChange}
                                onValidate={getErrorMessage}
                                className="percent"
                            />

                            <CurrencyInput
                                {...commonProps}
                                decimalScale={0}
                                label={(
                                    <If condition={isCompact}>
                                        <FormattedMessage
                                            id="DownPaymentInput.labels.dollars"
                                            defaultMessage="Down payment $"
                                        />
                                    </If>
                                )}
                                {...dollarsProps}
                                //! the following props can't be overridden as they are required for the component to function properly
                                name={`${name}.dollars`}
                                value={inputProps.value?.dollars}
                                onChange={handleDollarsChange}
                                onValidate={getErrorMessage}
                                className="dollars"
                            />
                        </ErrorMessageContainer>
                    </Container>
                );
            }}
        </InputController>
    );
}

DownPaymentInput.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string.isRequired,

    purchasePrice: PropTypes.number,
    value: PropTypes.shape({
        dollars: PropTypes.number,
        percent: PropTypes.number,
    }),

    /**
     * @param {({ dollars: number, percent: number }) => void} value
     */
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onValidityChange: PropTypes.func,
    onValidate: PropTypes.func,
    isRequired: PropTypes.bool,
    isDisabled: PropTypes.bool,
    // styling
    isInline: PropTypes.bool, // use inline layout
    isCompact: PropTypes.bool, // use compact labels
    className: PropTypes.string,
    inputClassName: PropTypes.string,
    // input customization
    percentProps: PropTypes.shape({
        isPercentSymbolVisible: PropTypes.bool,
        decimalScale: PropTypes.number,
        label: MessagePropType,
        placeholder: MessagePropType,
    }),
    dollarsProps: PropTypes.shape({
        decimalScale: PropTypes.number,
        label: MessagePropType,
        placeholder: MessagePropType,
    }),
    // errors
    errorMessageStyle: PropTypes.string,
    canShowInvalid: PropTypes.bool,
};

DownPaymentInput.defaultProps = {
    id: undefined,
    purchasePrice: undefined,
    value: undefined,
    onChange: undefined,
    onFocus: undefined,
    onBlur: undefined,
    onValidityChange: undefined,
    onValidate: undefined,
    isRequired: undefined,
    isDisabled: undefined,
    isInline: false,
    isCompact: false,
    percentProps: undefined,
    dollarsProps: undefined,
    className: undefined,
    inputClassName: undefined,
    errorMessageStyle: undefined,
    canShowInvalid: undefined,
};

/**
 * Calculates rounded percentage; needs to be rounded because FormattedNumberInput doesn't round numbers correctly.
 * Percentage will never be accurate so this is an approximation for display purposes anyways.
 * 
 * @param {number} dollars 
 * @param {number} purchasePrice - purchase price of 0 returns null
 * @returns {number | null} - percentage rounded to 2 decimal places
 */
function calculatePercent(dollars, purchasePrice) {
    // if purchase price is 0, we can't calculate a percentage
    if (typeof dollars === 'number' && purchasePrice) {
        return roundDownPaymentPercentage(dollars / purchasePrice * 100, 2);
    }

    return null;
}

function calculateDownPayment(percent, purchasePrice) {
    if (typeof percent === 'number' && purchasePrice) {
        return percent * purchasePrice / 100;
    }

    return null;
}

function useDownPaymentState({
    value,
    purchasePrice,
    getCustomErrorMessage,
    canShowInvalid,
}) {
    const state = useLocalObservable(() => ({
        _purchasePrice: purchasePrice,
        _canShowInvalid: canShowInvalid,

        get purchasePrice() {
            return this._purchasePrice;
        },
        set purchasePrice(newPurchasePrice) {
            this._purchasePrice = newPurchasePrice;

            // initial value might be invalid with updated purchase price
            if (this.value?.dollars < this.minimumDownPayment?.dollars) {
                this._value = null; // compute down payment
            }
        },

        get minimumDownPayment() {
            if (!this.purchasePrice) {
                return null; // can't calculate minimum down payment without purchase price
            }

            return {
                dollars: getMinimumDownPaymentDollars(this.purchasePrice),
                percent: roundDownPaymentPercentage(getMinimumDownPaymentPercent(this.purchasePrice), 2),
            };
        },

        _value: value,

        get value() {
            // purchase price of $0 is invalid too
            if (!this.purchasePrice || (this.dollars == null && this.percent == null)) {
                return null;
            }

            return {
                dollars: this.dollars ?? calculateDownPayment(this.percent, this.purchasePrice),
                percent: this.percent ?? calculatePercent(this.dollars, this.purchasePrice),
            };
        },
        set value(newValue) {
            if (newValue == null) {
                this._value = null;
            } else if (typeof newValue.dollars === 'number') {
                this.dollars = newValue.dollars;
            } else if (typeof newValue.percent === 'number') {
                this.percent = roundDownPaymentPercentage(newValue.percent, 2);
            }
        },

        get dollars() {
            return this._value?.dollars;
        },
        set dollars(newValue) {
            this._value = { dollars: newValue };
        },
        get percent() {
            return this._value?.percent;
        },
        set percent(newValue) {
            this._value = { percent: newValue };
        },

        getCustomErrorMessage,

        get canShowInvalid() {
            return this.purchasePrice != null && this._canShowInvalid;
        },
        
        get errorMessage() {
            // Can't validate down payment without purchase price
            // Validation is done on this.value so we can highlight both inputs when only one has an explicit value
            if (this.purchasePrice == null || this.value == null) {
                return null; //! NOTE: if input is required, InputController will still produce an error message
            }
            
            // We're only validating dollars to validate both inputs since one when is invalid, both are invalid.
            return getDownPaymentErrorMessage(this.value.dollars, this.purchasePrice) ?? this.getCustomErrorMessage?.(this.value) ?? null;
        },
    }));

    // Purchase price changes need to update before we compute the displayed error message or it causes error flashing
    // so we're using useLayoutEffect on the client because it runs before paint (ie. before the error message is displayed)
    // useSafeLayoutEffect will use useEffect on SSR
    // TODO: Is there a better way to do this? https://ratehub.atlassian.net/browse/EVR-3527
    useSafeLayoutEffect(
        () => {
            state.purchasePrice = purchasePrice;
        },
        [ purchasePrice ],
    );

    useEffect(
        () => {
            state.getCustomErrorMessage = getCustomErrorMessage;
        },
        [ getCustomErrorMessage ],
    );

    useEffect(
        () => {
            state._canShowInvalid = canShowInvalid;
        },
        [ canShowInvalid ]
    );

    useDeepEffect(
        () => {
            state.value = value;
        },
        [ value ]
    );

    return state;
}

/**
 * Like useEffect but does deep comparison of dependencies instead of shallow comparison.
 * 
 * Adapted from https://marcoghiani.com/blog/how-to-use-the-react-hook-usedeepeffect
 * 
 * @param {Function} callback 
 * @param {Array} dependencies 
 */
function useDeepEffect(callback, dependencies) {
    const isFirstMount = useRef(true);
    const previousDependencies = useRef(dependencies);

    useEffect(
        () => {
            const isSame = hasSameValues(previousDependencies.current, dependencies);

            // Callback should always fire on first mount or when dependencies change.
            if (isFirstMount.current || !isSame) {
                callback();
            }

            isFirstMount.current = false;
            previousDependencies.current = dependencies;
        },
        [ dependencies ]
    );
}

const DEFAULT_ERROR_MESSAGE = (
    <FormattedMessage
        id="DownPaymentInput.validators.default"
        defaultMessage="Enter a valid down payment"
    />
);

const INT_32_MAXIMUM = Math.pow(2, 31);

const Container = styled.div`
    > .input-container {
        display: flex;
        flex-direction: column;
        gap: ${Sizes.SPACING.HALF};
    }

    &.is-inline {
        > .input-container {
            flex-direction: row-reverse; /* $ before % */

            > .dollars {
                flex: 6.5;
            }

            > .percent {
                flex: 3.5;
            }
        }
    }
`;

function getDownPaymentErrorMessage(dollars, purchasePrice) {
    if (dollars > INT_32_MAXIMUM) {
        return (
            <FormattedMessage
                id="DownPaymentInput.validators.int32"
                defaultMessage="Character limit exceeded. Must be within 9 characters."
            />
        );
    }

    const minimumDownPaymentDollars = getMinimumDownPaymentDollars(purchasePrice);

    if (dollars < minimumDownPaymentDollars) {
        const minimumDownPaymentPercent = getMinimumDownPaymentPercent(purchasePrice) / 100; // must be a ratio for intl formatting

        return (
            <FormattedMessage
                id="DownPaymentInput.validators.minimumDownPayment"
                defaultMessage="A minimum down payment of {minimumDownPaymentDollars, number, CAD0} ({minimumDownPaymentPercent, number, percent1}) is required for this purchase price"
                values={{
                    minimumDownPaymentDollars,
                    minimumDownPaymentPercent,
                }}
            />
        );
    }

    if (dollars > purchasePrice) {
        return (
            <FormattedMessage
                id="DownPaymentInput.validators.maximumDownPayment"
                defaultMessage="Enter a down payment percent less than 100%"
            />
        );
    }
}

export default observer(DownPaymentInput);